/**
 * `CLOCK UNVERIFIED` — §9.2.8's pill.
 *
 * > A visibly uncertain countdown beats a confidently wrong one, on a product
 * > where the last five seconds carry most of the volume.
 *
 * Two conditions, one pill:
 *
 * - **`UNVERIFIED`.** Fewer than three of ten samples survived filtering, so the
 *   clock is running at `θ = 0` — which is not the same as running correctly.
 * - **`|θ| > 250 ms`.** The offset is known and is larger than the tolerance
 *   D9 sets. The countdown is being slewed, and the ticket soft-disables
 *   one-click entry for the final 500 ms (`order-validation.ts`'s
 *   `CLOCK_RESYNC`); this is the same fact, said where the number is rendered.
 *
 * **It also asks for a re-sync**, which is the half of §9.2.8 that is easy to
 * forget: a pill that appears and never clears itself is a pill the user learns
 * to ignore. Once per crossing, not on a timer — invariant 8, and the clock
 * already has a 15-minute backstop and a 5-second pong sample.
 *
 * A leaf component subscribing `runtime.state` rather than a prop off the route,
 * for invariant 2's reason: the strip re-rendering on a clock transition is
 * cheap, and the route re-rendering on one is the chart and the ladder.
 */

import { useEffect } from 'react'
import { useRuntime, useRuntimeState } from '@/app/use-runtime'
import { Pill } from '@/components/primitives'

export function ClockPill() {
  const runtime = useRuntime()
  const state = useRuntimeState()
  // `measuredOffsetMs` is not in `RuntimeState` and does not want to be: it moves
  // on every pong, and a store that published it would re-render this component
  // at 0.2 Hz forever to change nothing. The *status* is what transitions.
  const unverified = state.clock === 'UNVERIFIED' || runtime.clock.offsetExceedsTolerance
  const show = state.phase === 'READY' && unverified

  useEffect(() => {
    if (!show) return
    // One attempt per crossing. `resync` shares an in-flight run, so a wake and
    // this landing together is one handshake rather than two.
    void runtime.resyncClock()
  }, [show, runtime])

  if (!show) return null
  return (
    <Pill tone="warning" className="uppercase">
      CLOCK UNVERIFIED
    </Pill>
  )
}
